import Api from '@/services/Api';

// Server-side filters are implemented in backend.
// Keep legacy FE filtering logic behind a flag for safe rollback.
const USE_LEGACY_LOCAL_FILTERS = false;

function toISODateOnly(value) {
  try {
    const d = new Date(value);
    // Invalid Date -> toISOString throws
    return d.toISOString().slice(0, 10);
  } catch (e) {
    return null;
  }
}

function isTranslationKey(value) {
  return typeof value === 'string' && /^\$t\(.+\)$/.test(value);
}

function getStatusDescription(value) {
  if (!value || typeof value !== 'object') return null;
  return (
    value.EstadoDescrp ||
    value.state ||
    (!isTranslationKey(value.descripcion) ? value.descripcion : null) ||
    null
  );
}

function findOptionById(options, id) {
  if (!Array.isArray(options)) return null;
  return options.find((option) => String(option.id) === String(id));
}

function normalizeStatusFilter(value, options = []) {
  if (value === null || typeof value === 'undefined') return null;
  if (typeof value === 'object') {
    return (
      getStatusDescription(value) ||
      findOptionById(options, value.id)?.EstadoDescrp ||
      findOptionById(options, value.id)?.state ||
      null
    );
  }
  const option = findOptionById(options, value);
  return getStatusDescription(option) || value;
}

function normalizeDeliveryTypeFilter(value) {
  if (value === null || typeof value === 'undefined') return null;
  if (typeof value === 'object') {
    return (
      value.TipoEntrega ||
      value.id ||
      value.TipoEntregaDescrp ||
      value.label ||
      null
    );
  }
  return value;
}

export default {
  namespaced: true,
  state: {
    materials: [],
    solicitudesMateriales: [],
    materialsSelected: [],
    materialsRequest: [],
    materialsRequestResponse: {},
    acceptRequestPopup: false,
    confirmPopup: false,
    requestCode: '',
  },
  actions: {
    async registerMaterialRequest({ commit }, data) {
      try {
        const xhr = await Api.get_alta_solicitud(data);
        const response = JSON.parse(xhr.response);
        commit('setMaterialsRequestResponse', response);
        commit('setRequestCode', response.TipoSolicitud);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchMaterials({ commit }, CodPlantaVenta) {
      try {
        const xhr = await Api.get_listado_articulos(CodPlantaVenta);
        const response = JSON.parse(xhr.response);
        commit('setMaterials', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchSolicitudesMateriales(
      { commit, rootState },
      estado = []
    ) {
      try {
        // Filters come from MaterialRequest filter UI via EventBus payload:
        // [estadoDescrp, tipoEntrega, fromDate, toDate]
        const [estadoRaw, tipoEntregaRaw, fromRaw, toRaw] =
          Array.isArray(estado) ? estado : [];
        const estadoDescrp = normalizeStatusFilter(
          estadoRaw,
          rootState.materialRequestFilters?.materialStates
        );
        const tipoEntrega =
          normalizeDeliveryTypeFilter(tipoEntregaRaw);

        const queryParams = {
          estadoDescrp: estadoDescrp || undefined,
          tipoEntrega: tipoEntrega || undefined,
          from: fromRaw ? toISODateOnly(fromRaw) : undefined,
          to: toRaw ? toISODateOnly(toRaw) : undefined,
        };

        const xhr = await Api.get_listado_solicitudes_materiales(
          rootState.contact.company,
          queryParams
        );
        const response = JSON.parse(xhr.response);

        let filteredResponse = response;

        // Normalize FechaSolicitud for UI consistency (keep only YYYY-MM-DD).
        filteredResponse.forEach((solicitud) => {
          if (solicitud.FechaSolicitud) {
            const [fecha] = solicitud.FechaSolicitud.split('T');
            solicitud.FechaSolicitud = fecha;
          }
        });

        // Legacy local filters (OFF by default): backend already filters + orders by FechaSolicitud DESC.
        if (USE_LEGACY_LOCAL_FILTERS) {
          if (Array.isArray(estado) && estado.length > 0) {
            const [estadoDescrpLocalRaw, tipoEntregaLocalRaw] =
              estado;
            const estadoDescrpLocal = normalizeStatusFilter(
              estadoDescrpLocalRaw,
              rootState.materialRequestFilters?.materialStates
            );
            const tipoEntregaLocal = normalizeDeliveryTypeFilter(
              tipoEntregaLocalRaw
            );
            filteredResponse = filteredResponse.filter((solicitud) => {
              return (
                (!estadoDescrpLocal ||
                  solicitud.EstadoDescrp === estadoDescrpLocal) &&
                (!tipoEntregaLocal ||
                  solicitud.TipoEntrega === tipoEntregaLocal)
              );
            });
          }
          if (estado.length >= 4 && estado[2] && estado[3]) {
            const fechaInicio = toISODateOnly(estado[2]);
            const fechaFin = toISODateOnly(estado[3]);
            if (fechaInicio && fechaFin) {
              filteredResponse = filteredResponse.filter(
                (solicitud) => {
                  if (solicitud.FechaSolicitud) {
                    const fechaSolicitud =
                      solicitud.FechaSolicitud;
                    return (
                      fechaSolicitud >= fechaInicio &&
                      fechaSolicitud <= fechaFin
                    );
                  }
                  return false;
                }
              );
            }
          }
          filteredResponse.sort(
            (a, b) =>
              new Date(b.FechaSolicitud) -
              new Date(a.FechaSolicitud)
          );
        }

        commit('setSolicitudesMateriales', filteredResponse);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    setSolicitudesMateriales({ commit }, value) {
      commit('setSolicitudesMateriales', value);
    },
    setMaterials({ commit }, value) {
      commit('setMaterials', value);
    },
    setMaterialsSelected({ commit, state }, value) {
      const updatedMaterials = [...state.materialsSelected];
      value.forEach((newMaterial) => {
        const existingMaterial = updatedMaterials.find(
          (material) =>
            material.CodArticulo === newMaterial.CodArticulo
        );
        if (existingMaterial) {
          existingMaterial.Cantidad += 1;
        } else {
          updatedMaterials.push({ ...newMaterial, Cantidad: 1 });
        }
      });
      commit('setMaterialsSelected', updatedMaterials);
    },
    setMaterialsRequest({ commit }, value) {
      commit('setMaterialsRequest', value);
    },
    setMaterialsRequestResponse({ commit }, value) {
      commit('setMaterialsRequestResponse', value);
    },
    setAcceptRequestPopup({ commit }, value) {
      commit('setAcceptRequestPopup', value);
    },
    setConfirmPopup({ commit }, value) {
      commit('setConfirmPopup', value);
    },
    setRequestCode({ commit }, value) {
      commit('setRequestCode', value);
    },
    deleteMaterialsSelected({ commit, state }, value) {
      const materials = state.materialsSelected.filter((material) => {
        return material.CodArticulo !== value;
      });
      commit('setMaterialsSelected', materials);
    },
    deleteAllMaterailsSelected({ commit }) {
      commit('setMaterialsSelected', []);
    },
  },
  mutations: {
    setMaterials(state, value) {
      state.materials = value;
    },
    setSolicitudesMateriales(state, value) {
      state.solicitudesMateriales = value;
    },
    setMaterialsSelected(state, value) {
      state.materialsSelected = value;
    },
    setMaterialsRequest(state, value) {
      state.materialsRequest = value;
    },
    setAcceptRequestPopup(state, value) {
      state.acceptRequestPopup = value;
    },
    setConfirmPopup(state, value) {
      state.confirmPopup = value;
    },
    setMaterialsRequestResponse(state, value) {
      state.materialsRequestResponse = value;
    },
    setRequestCode(state, value) {
      state.requestCode = value;
    },
    deleteMaterialsSelected(state, value) {
      state.materialsSelected = value;
    },
    deleteAllMaterailsSelected(state) {
      state.materialsSelected = [];
    },
    resetData(state) {
      state.materials = [];
      state.solicitudesMateriales = [];
      state.materialsSelected = [];
      state.materialsRequest = [];
      state.materialsRequestResponse = [];
      state.acceptRequestPopup = false;
      state.confirmPopup = false;
    },
  },
};
